/**
 * App Downloads tile — iOS App Store + Google Play install/download counts.
 *
 * Backend tile `app_downloads` (admin-analytics-dashboard.ts) sources these
 * from a daily collector's SSM snapshot. GRACEFUL by contract: any store value
 * may be null (pipe not wired, or the store isn't reporting yet — iOS stays
 * null until the app is released from review), rendered as an em-dash.
 *
 * `last_day` is the count ON `as_of`, the latest day THAT store has reported
 * (scripts/app-downloads/collect.mjs). The stores report on different days, so
 * each row carries its own date: never "today", and never one shared "As of"
 * (on 2026-10-10 that showed iOS's Oct 9 over Play's Oct 5).
 */
import { Apple, Smartphone } from 'lucide-react';
import { formatBirthdate, formatDate, formatNumber } from '@/lib/utils';

interface StoreDownloads {
  total: number | null;
  last_day: number | null;
  as_of: string | null;
}

interface Props {
  data: {
    ios: StoreDownloads | null;
    android: StoreDownloads | null;
    collected_at: string | null;
  };
}

const DASH = '—';

function fmt(n: number | null): string {
  return typeof n === 'number' ? formatNumber(n) : DASH;
}

function StoreRow({
  icon,
  label,
  store,
}: {
  icon: React.ReactNode;
  label: string;
  store: StoreDownloads | null;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums">{fmt(store?.total ?? null)}</span>
        {typeof store?.last_day === 'number' && store.as_of && (
          // formatBirthdate is the calendar-date formatter: `as_of` is a day, not an
          // instant, so it must not shift to the previous day west of UTC.
          <span className="text-xs text-muted-foreground">
            +{formatNumber(store.last_day)} on {formatBirthdate(store.as_of)}
          </span>
        )}
      </div>
    </div>
  );
}

export function AppDownloadsTile({ data }: Props) {
  return (
    <div className="bg-card border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">App downloads</div>
      <div className="mt-2 space-y-1">
        <StoreRow icon={<Apple className="h-4 w-4" />} label="iOS App Store" store={data.ios} />
        <StoreRow icon={<Smartphone className="h-4 w-4" />} label="Google Play" store={data.android} />
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        {data.collected_at ? `Collected ${formatDate(data.collected_at)}` : 'Awaiting first collection'}
      </div>
    </div>
  );
}
